import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export type FulfillmentMethod = "FBS" | "FBP" | "FBA";

export interface DeliveryFeesState {
  method: FulfillmentMethod;

  // FBA — calculated from subtotal tiers
  fbaFee: number;

  // FBP — fetched from partner (DHL) rates API
  fbpFee: number;
  fbpRates: FbpRate[];
  fbpLoading: boolean;
  fbpError: string | null;
  selectedFbpRate: FbpRate | null;

  // FBS — fetched from DHL rates API
  fbsFee: number;
  fbsRates: FbsRate[];
  fbsLoading: boolean;
  fbsError: string | null;
  selectedFbsRate: FbsRate | null;

  // Final resolved delivery fee (whichever method is active)
  resolvedFee: number;
}

export interface FbpRate {
  productName: string;
  totalPrice: number;
  currency: string;
  deliveryTime?: string;
  serviceCode?: string;
}

export interface FbsRate {
  productName: string;
  totalPrice: number;
  currency: string;
  deliveryTime?: string;
  serviceCode?: string;
}

// ─────────────────────────────────────────────
// FBA FEE TIERS
// ─────────────────────────────────────────────

/**
 * FBA fee is a percentage of the order subtotal (before delivery).
 * Armerix warehouses & handles the order — higher subtotal = lower % fee.
 */
export const calculateFbaFee = (subtotal: number): number => {
  let fee: number;

  if (subtotal > 5000)       fee = subtotal * 0.04;   // 4%
  else if (subtotal > 2000)  fee = subtotal * 0.055;  // 5.5%
  else if (subtotal > 1000)  fee = subtotal * 0.07;   // 7%
  else if (subtotal > 500)   fee = subtotal * 0.09;   // 9%
  else if (subtotal > 200)   fee = subtotal * 0.11;   // 11%
  else                        fee = subtotal * 0.13;   // 13% — small orders cost more to handle

  return parseFloat(fee.toFixed(2));
};

// ─────────────────────────────────────────────
// ASYNC THUNKS
// ─────────────────────────────────────────────

const DHL_RATES_URL     = "https://europe-west3-armerix.cloudfunctions.net/getDhlRates";
const PARTNER_RATES_URL = "https://europe-west3-armerix.cloudfunctions.net/getDhlRates"; // same endpoint, partner network

/** Set method + recalculate FBA fee synchronously */
export const setFulfillmentMethod = createAsyncThunk(
  "deliveryFees/setFulfillmentMethod",
  async ({ method, subtotal }: { method: FulfillmentMethod; subtotal: number }) => {
    const fbaFee = method === "FBA" ? calculateFbaFee(subtotal) : 0;
    return { method, fbaFee };
  }
);

/** Fetch FBP (partner / DHL partner network) rates */
export const fetchFbpRates = createAsyncThunk(
  "deliveryFees/fetchFbpRates",
  async (
    { postalCode, countryCode, weight = 1 }: { postalCode: string; countryCode: string; weight?: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await fetch(PARTNER_RATES_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postalCode, countryCode, weight, network: "FBP" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const products: FbpRate[] = Array.isArray(data)
        ? data
        : (data.products ?? []);
      return products.map((p: any) => ({
        productName:  p.productName  ?? p.name ?? "Partner Express",
        totalPrice:   p.totalPrice   ?? p.price ?? 0,
        currency:     p.currency     ?? "EUR",
        deliveryTime: p.deliveryTime ?? p.estimatedDeliveryTime ?? "",
        serviceCode:  p.serviceCode  ?? "",
      })) as FbpRate[];
    } catch (err: any) {
      return rejectWithValue(err.message ?? "Failed to fetch partner rates");
    }
  }
);

/** Fetch FBS (DHL Express) rates */
export const fetchFbsRates = createAsyncThunk(
  "deliveryFees/fetchFbsRates",
  async (
    { postalCode, countryCode, weight = 1 }: { postalCode: string; countryCode: string; weight?: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await fetch(DHL_RATES_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ postalCode, countryCode, weight }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const products: FbsRate[] = Array.isArray(data)
        ? data
        : (data.products ?? []);
      return products.map((p: any) => ({
        productName:  p.productName  ?? p.name ?? "DHL Express",
        totalPrice:   p.totalPrice   ?? p.price ?? 0,
        currency:     p.currency     ?? "EUR",
        deliveryTime: p.deliveryTime ?? p.estimatedDeliveryTime ?? "",
        serviceCode:  p.serviceCode  ?? "",
      })) as FbsRate[];
    } catch (err: any) {
      return rejectWithValue(err.message ?? "Failed to fetch DHL rates");
    }
  }
);

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────

const initialState: DeliveryFeesState = {
  method: "FBS",

  fbaFee: 0,

  fbpFee: 0,
  fbpRates: [],
  fbpLoading: false,
  fbpError: null,
  selectedFbpRate: null,

  fbsFee: 0,
  fbsRates: [],
  fbsLoading: false,
  fbsError: null,
  selectedFbsRate: null,

  resolvedFee: 0,
};

const deliveryFeesSlice = createSlice({
  name: "deliveryFees",
  initialState,
  reducers: {
    selectFbpRate(state, action: PayloadAction<FbpRate>) {
      state.selectedFbpRate = action.payload;
      state.fbpFee          = action.payload.totalPrice;
      if (state.method === "FBP") state.resolvedFee = action.payload.totalPrice;
    },
    selectFbsRate(state, action: PayloadAction<FbsRate>) {
      state.selectedFbsRate = action.payload;
      state.fbsFee          = action.payload.totalPrice;
      if (state.method === "FBS") state.resolvedFee = action.payload.totalPrice;
    },
    resetDeliveryFees() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // setFulfillmentMethod
    builder.addCase(setFulfillmentMethod.fulfilled, (state, action) => {
      state.method  = action.payload.method;
      state.fbaFee  = action.payload.fbaFee;

      if (action.payload.method === "FBA") {
        state.resolvedFee = action.payload.fbaFee;
      } else if (action.payload.method === "FBP") {
        state.resolvedFee = state.fbpFee;
      } else {
        state.resolvedFee = state.fbsFee;
      }
    });

    // fetchFbpRates
    builder.addCase(fetchFbpRates.pending,  (state) => { state.fbpLoading = true;  state.fbpError = null; });
    builder.addCase(fetchFbpRates.rejected, (state, action) => {
      state.fbpLoading = false;
      state.fbpError   = action.payload as string;
    });
    builder.addCase(fetchFbpRates.fulfilled, (state, action) => {
      state.fbpLoading = false;
      state.fbpRates   = action.payload;
      // Auto-select cheapest
      if (action.payload.length > 0) {
        const cheapest = [...action.payload].sort((a, b) => a.totalPrice - b.totalPrice)[0];
        state.selectedFbpRate = cheapest;
        state.fbpFee          = cheapest.totalPrice;
        if (state.method === "FBP") state.resolvedFee = cheapest.totalPrice;
      }
    });

    // fetchFbsRates
    builder.addCase(fetchFbsRates.pending,  (state) => { state.fbsLoading = true;  state.fbsError = null; });
    builder.addCase(fetchFbsRates.rejected, (state, action) => {
      state.fbsLoading = false;
      state.fbsError   = action.payload as string;
    });
    builder.addCase(fetchFbsRates.fulfilled, (state, action) => {
      state.fbsLoading = false;
      state.fbsRates   = action.payload;
      // Auto-select cheapest
      if (action.payload.length > 0) {
        const cheapest = [...action.payload].sort((a, b) => a.totalPrice - b.totalPrice)[0];
        state.selectedFbsRate = cheapest;
        state.fbsFee          = cheapest.totalPrice;
        if (state.method === "FBS") state.resolvedFee = cheapest.totalPrice;
      }
    });
  },
});

export const { selectFbpRate, selectFbsRate, resetDeliveryFees } = deliveryFeesSlice.actions;
export default deliveryFeesSlice.reducer;
